import { useState } from 'react';
import { queryString } from '@olivierpascal/helpers';
import { useTranslation } from 'react-i18next';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type { ILogInAsFormProps, ILogInAsFormValues } from './LogInAsForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { getCurrentLocation, useNavigate } from '~/components/utils/Navigation';
import { useLogInAsMutation } from '~/lib/api/modules/Auth/hooks/useLogInAsMutation';
import { LogInAsForm } from './LogInAsForm';

export type ILogInAsFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose'
> &
  ILogInAsFormProps;

export const LogInAsFormDialog: React.FC<ILogInAsFormDialogProps> = (props) => {
  const { opened, onClose, ...other } = props;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const logInAsMutation = useLogInAsMutation();
  const navigate = useNavigate();

  const handleClose = (): void => {
    setIsSubmitting(false);
    onClose?.();
  };

  const handleSubmit = async (values: ILogInAsFormValues): Promise<void> => {
    setIsSubmitting(true);

    return logInAsMutation
      .mutateAsync({
        body: {
          email: values.emailAddress!,
        },
      })
      .then((authorization) => {
        const currentUrl = new URL(getCurrentLocation());
        const switchUrl = `^/sessions/switch/${authorization.sessionId}?${queryString.build(
          { nextUrl: currentUrl.pathname },
        )}`;
        navigate(switchUrl);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <FormDialog
      opened={opened}
      onClose={handleClose}
      headline={t('LogInAsFormDialog.headline')}
      submitButtonLabel={t('common.open')}
      size="md"
      submitting={isSubmitting}
    >
      {({ formId }) => (
        <LogInAsForm
          id={formId}
          extended
          hideActionBar
          onSubmit={handleSubmit}
          onFailure={() => setIsSubmitting(false)}
          {...other}
        />
      )}
    </FormDialog>
  );
};
